import React from 'react';
import { css, keyframes } from '@emotion/css';

import { useAnimatedNumber } from '@lyearn/core/hooks/useAnimatedNumber';
import { StarAiFill } from '@lyearn/icons';
import { Box, Stack, Typography } from '@lyearn/ui';

interface GeneratingVideoProps {
  completedCount?: number;
  totalCount?: number;
  thumbnailUrl?: string;
  progress?: number | null;
}

const rotateClockwiseAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
`;

const rotateClockwise = css`
  animation: ${rotateClockwiseAnimation} 1.8s linear infinite;
  transform-origin: center;
`;

const GeneratingVideo = (props: GeneratingVideoProps) => {
  const { completedCount = 0, totalCount = 0, thumbnailUrl, progress } = props;

  const [stableBackendPercent, setStableBackendPercent] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (typeof progress === 'number' && !Number.isNaN(progress)) {
      const next = Math.max(0, Math.min(100, Math.round(progress)));
      setStableBackendPercent((prev) => (prev == null ? next : Math.max(prev, next)));
    }
  }, [progress]);

  const fallbackPercent =
    stableBackendPercent == null && totalCount > 0
      ? Math.round((completedCount / totalCount) * 100)
      : 0;

  const progressPercent = stableBackendPercent ?? fallbackPercent;
  const animatedPercent = useAnimatedNumber(progressPercent, {
    duration: 60,
    type: 'integer',
  });

  return (
    <Box className="border-border border rounded-12 overflow-hidden">
      {/* Blurred thumbnail area */}
      <Box
        className="relative overflow-hidden"
        style={{ aspectRatio: '16/9', background: '#1a1a2e' }}>
        {/* Background thumbnail image with blur */}
        {thumbnailUrl && (
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${thumbnailUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(16px)',
              transform: 'scale(1.1)',
            }}
          />
        )}
        {/* Dark overlay */}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.45)',
          }}
        />

        {/* Center content */}
        <Stack
          alignItems="center"
          className="relative z-10"
          direction="col"
          gap={16}
          justifyContent="center"
          style={{ minHeight: 320 }}>
          <StarAiFill
            className={`${rotateClockwise} text-text-white`}
            style={{ width: '4.4rem', height: '4.4rem' }}
          />

          <Typography
            className="font-inter font-bold text-12 leading-20 tracking-[0.1em] text-center uppercase"
            color="text-text-white">
            GENERATING VIDEO
          </Typography>
        </Stack>

        {/* Bottom progress bar area */}
        <Stack
          alignItems="center"
          className="absolute bottom-0 left-0 right-0 z-10 px-16 pb-16"
          direction="row"
          gap={12}>
          <Typography
            className="font-inter font-bold text-14 leading-22 tracking-normal"
            color="text-text-white">
            {animatedPercent}%
          </Typography>
          <Box
            className="flex-1"
            style={{
              height: 4,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              overflow: 'hidden',
            }}>
            <Box
              style={{
                height: '100%',
                width: `${animatedPercent}%`,
                backgroundColor: 'white',
                borderRadius: 2,
                transition: 'width 0.3s ease-out',
              }}
            />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default GeneratingVideo;
